// The `error_hygiene.pass` row — ONE per weekly pass, the record a customer reads back
// (`GET /v1/integrations` `hygiene_last_pass`). A STORED shape (`audit_events.output`), so
// it is a one-way door: a field added later is optional, a field removed is unreadable rows.
//
// THE COUNTS ARE READ OFF THE CHAIN, never off the model's word. `tallyHygieneWrites` reads
// the session's settled `tool.call.ok` rows: a write the tracker refused, or one whose
// request failed after its write-ahead record, settled `tool.call.err` and counts nothing.
// The volumes are the platform's own `errors.projects.list` reads at the pass's start and
// end, not the session's.

import { z } from 'zod';

/** How a pass ended. A new ending is a new member here, never a second boolean. */
export const ErrorHygienePassOutcome = {
  /** The session ran to its end; the counts say what it changed (possibly nothing). */
  Completed: 'completed',
  /** The session ended failed or escalated (its terminal row says why); counts still stand. */
  Failed: 'failed',
  /** The tracker could not be opened (`refusal` names why); no session ran. */
  TrackerRefused: 'tracker_refused',
} as const;
export type ErrorHygienePassOutcome =
  (typeof ErrorHygienePassOutcome)[keyof typeof ErrorHygienePassOutcome];

const CountSchema = z.number().int().nonnegative();

/**
 * The tracker's unresolved volume, summed over the projects the connection reaches. A
 * total is `null` when any project's tracker reported no number for it: a partial sum
 * would read as a measurement it is not.
 */
export const ErrorHygieneVolumeSchema = z.object({
  projects: CountSchema,
  unresolved: CountSchema.nullable(),
  stale: CountSchema.nullable(),
  active: CountSchema.nullable(),
});
export type ErrorHygieneVolume = z.infer<typeof ErrorHygieneVolumeSchema>;

export const ErrorHygienePassSchema = z.object({
  /** The ISO week the pass ran for (`2026W45`), the job's own key. */
  week: z.string().min(1).max(16),
  outcome: z.enum(ErrorHygienePassOutcome),
  /** Why the tracker could not be opened (`ErrorTrackerRefusal.kind`); null otherwise. */
  refusal: z.string().max(64).nullable(),
  before: ErrorHygieneVolumeSchema.nullable(),
  /** Null when the end read could not be made (the tracker refused or failed by then). */
  after: ErrorHygieneVolumeSchema.nullable(),
  /** Issues resolved (quiet, re-proved by the tool). */
  resolved: CountSchema,
  /** Issues folded into another by a re-proved merge. */
  merged: CountSchema,
  /** Rule appends that changed a project's rules. */
  rules: CountSchema,
  /** Source-fix asks the pass filed that were new rows. */
  asks: CountSchema,
});
export type ErrorHygienePass = z.infer<typeof ErrorHygienePassSchema>;

/** One project's counts, as `errors.projects.list` answers them. */
export type ErrorHygieneProjectVolume = {
  readonly unresolved: number | null;
  readonly stale: number | null;
  readonly active: number | null;
};

const sumOrNull = (values: ReadonlyArray<number | null>): number | null =>
  values.some((v) => v === null) ? null : values.reduce<number>((a, v) => a + (v ?? 0), 0);

/** The org-wide volume of one `errors.projects.list` answer. */
export function errorHygieneVolume(
  projects: ReadonlyArray<ErrorHygieneProjectVolume>,
): ErrorHygieneVolume {
  return {
    projects: projects.length,
    unresolved: sumOrNull(projects.map((p) => p.unresolved)),
    stale: sumOrNull(projects.map((p) => p.stale)),
    active: sumOrNull(projects.map((p) => p.active)),
  };
}

/** The tools whose settled calls a pass counts. Spelled here because domain imports no tool. */
export const ERROR_HYGIENE_COUNTED_TOOLS = [
  'errors.issues.resolve',
  'errors.issues.merge',
  'errors.rules.append',
  'errors.fix.file',
] as const;

/** One settled `tool.call.ok` row: the tool and its output. */
export type SettledToolCall = {
  readonly tool: string | null;
  readonly output: Readonly<Record<string, unknown>> | null;
};

const arrayLength = (value: unknown): number => (Array.isArray(value) ? value.length : 0);

/**
 * What the pass changed, from its own settled `tool.call.ok` rows. Each output is the
 * tool's own (`@developerz/tools` `errors/write-tools.ts`, `errors/fix-tool.ts`):
 * `resolved: string[]`, a merge's `children: string[]`, a rule append's `outcome`, an
 * ask's `outcome`. Only a NEW ask counts: `existing` means the same issue's fix was
 * already on file, and counting it would re-report last week's ask as this week's work.
 */
export function tallyHygieneWrites(
  rows: ReadonlyArray<SettledToolCall>,
): Pick<ErrorHygienePass, 'resolved' | 'merged' | 'rules' | 'asks'> {
  let resolved = 0;
  let merged = 0;
  let rules = 0;
  let asks = 0;
  for (const row of rows) {
    const out = row.output ?? {};
    switch (row.tool) {
      case 'errors.issues.resolve':
        resolved += arrayLength(out['resolved']);
        break;
      case 'errors.issues.merge':
        merged += arrayLength(out['children']);
        break;
      case 'errors.rules.append':
        if (out['outcome'] === 'appended') rules += 1;
        break;
      case 'errors.fix.file':
        if (out['outcome'] === 'created') asks += 1;
        break;
    }
  }
  return { resolved, merged, rules, asks };
}
